import {
  ITransferStockDTO,
  ITransferStockItemDTO
} from 'modules/inbound/transfer/infrastructure/dto/TransferStockDTO';
import LocaleService from 'infrastructure/i18n/LocaleService';
import { TransferText } from '../infrastructure/i18n/TransferText';
import { TransferTranslations } from '../infrastructure/i18n/typing';
import { IPagination } from 'modules/inbound/shared/domain/IPagination';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';
import { CriticalStockType } from 'modules/shared/domain/enums';
import { NO_DATA } from 'modules/inbound/shared/domain/NameValues';

const transferTranslations: TransferTranslations = {
  ...TransferText[LocaleService.getLanguage()]
};

export interface ITransferStock {
  lastUpdate: string;
  stockLocationItems: ITransferStockItem[];
  columns: ColumnsInfo[];
}

export interface ITransferStockItem {
  code: string;
  description: string;
  locations: string[];
  warehouse: string;
  warehouseType: string;
  handlingUnits: string[];
  handlingUnitsCount: number;
  stocks: number[];
  factoryCoverage: string;
  supplyContainer: string;
  transportContainer: string;
  basicContainer: string;
  criticalStockType: CriticalStockType | null;
  coverageWithoutStock: string;
  supplyType: string;
  containerType: string;
  packagingMaterial: string;
  isPlanned: boolean;
  notTransferredInLastThreeMonths: boolean;
}

export interface ColumnsInfo {
  key: string;
  uiKey: string;
  value: string;
  sortable: boolean;
  isVisibleCheck: boolean;
}

export enum TransferStockColumnKeys {
  code = 'code',
  description = 'description',
  warehouse = 'warehouse',
  warehouseType = 'warehouseType',
  locations = 'locations',
  handlingUnits = 'handlingUnits',
  handlingUnitsCount = 'handlingUnitsCount',
  stocks = 'stocks',
  factoryCoverage = 'factoryCoverage',
  coverageWithoutStock = 'coverageWithoutStock',
  isPlanned = 'isPlanned',
  supplyType = 'supplyType',
  containerType = 'containerType',
  packagingMaterial = 'packagingMaterial',
  basicContainer = 'basicContainer',
  transportContainer = 'transportContainer',
  supplyContainer = 'supplyContainer'
}

const SORTABLE_COLUMNS = new Set([
  TransferStockColumnKeys.code,
  TransferStockColumnKeys.description,
  TransferStockColumnKeys.warehouse,
  TransferStockColumnKeys.warehouseType,
  TransferStockColumnKeys.factoryCoverage,
  TransferStockColumnKeys.coverageWithoutStock,
  TransferStockColumnKeys.supplyType,
  TransferStockColumnKeys.basicContainer,
  TransferStockColumnKeys.transportContainer,
  TransferStockColumnKeys.supplyContainer,
  TransferStockColumnKeys.containerType,
  TransferStockColumnKeys.packagingMaterial,
  TransferStockColumnKeys.isPlanned
]);

const RIGHT_ALIGNED_COLUMNS = [
  TransferStockColumnKeys.coverageWithoutStock,
  TransferStockColumnKeys.factoryCoverage
];

class TransferStock implements ITransferStock {
  lastUpdate: string;
  stockLocationItems: ITransferStockItem[];
  columns: ColumnsInfo[];
  pagination: IPagination;

  constructor({
    lastUpdate,
    stockLocationItems,
    columns,
    pagination
  }: TransferStock) {
    this.lastUpdate = lastUpdate;
    this.stockLocationItems = stockLocationItems;
    this.columns = columns;
    this.pagination = pagination;
  }
  private static _camelToSnakeCase(key) {
    return key.replace(/([A-Z])/g, '_$1').toLowerCase();
  }

  /** Get coverage in format 2d 4h 24min */
  private static _getCoverageTimeFormat(coverage) {
    if (coverage !== null && coverage > 0) {
      const days = Math.floor(coverage);
      const hours = Math.round((coverage - Math.floor(coverage)) * 24);
      const remainingHours = (coverage - Math.floor(coverage)) * 24 - hours;
      const minutes = Math.round(remainingHours * 60);
      if (minutes > 0) {
        return `${days}d ${hours}h ${minutes}min`;
      } else if (hours > 0) {
        return `${days}d ${hours}h`;
      }
      return `${days}d`;
    }
    if (coverage === 0) {
      return `0d`;
    }
    return NO_DATA;
  }

  static generateFromDTO({
    last_update,
    stock_location_item,
    pagination
  }: ITransferStockDTO): TransferStock {
    return new TransferStock({
      lastUpdate: last_update
        ? `${GetDateFormatedService.getDateFormated(
            last_update,
            'de-DE'
          )} | ${GetDateFormatedService.getTimeFormated(last_update)}`
        : NO_DATA,
      stockLocationItems: stock_location_item.map(
        ({
          code,
          description,
          locations,
          warehouse,
          warehouse_type,
          handling_units,
          stocks,
          factory_coverage,
          supply_container,
          transport_container,
          basic_container,
          critical_stock_type,
          coverage_without_stock,
          supply_type,
          container_type,
          packaging_material,
          is_planned,
          not_transferred_in_last_three_months
        }: ITransferStockItemDTO): ITransferStockItem => ({
          code,
          description: description ?? NO_DATA,
          locations,
          warehouse,
          warehouseType: warehouse_type,
          handlingUnits: handling_units ?? [],
          handlingUnitsCount: (handling_units ?? []).length,
          stocks: stocks ?? [],
          factoryCoverage: this._getCoverageTimeFormat(factory_coverage),
          supplyContainer: supply_container,
          transportContainer: transport_container,
          basicContainer: basic_container,
          criticalStockType: critical_stock_type as CriticalStockType,
          coverageWithoutStock: this._getCoverageTimeFormat(
            coverage_without_stock
          ),
          supplyType: supply_type ?? NO_DATA,
          containerType: container_type ?? NO_DATA,
          packagingMaterial: packaging_material ?? NO_DATA,
          isPlanned: is_planned,
          notTransferredInLastThreeMonths:
            not_transferred_in_last_three_months === 1
        })
      ),
      columns: Object.keys(TransferStockColumnKeys).map((key) => ({
        key: this._camelToSnakeCase(key),
        uiKey: key,
        value: transferTranslations[key] || key,
        sortable: SORTABLE_COLUMNS.has(TransferStockColumnKeys[key]),
        isVisibleCheck: true,
        tooltipInfo:
          key === TransferStockColumnKeys.coverageWithoutStock &&
          transferTranslations.coverageStoredOutsideTranfer,
        draggable:
          key !== TransferStockColumnKeys.code &&
          key !== TransferStockColumnKeys.locations &&
          key !== TransferStockColumnKeys.handlingUnits &&
          key !== TransferStockColumnKeys.handlingUnitsCount,
        align:
          RIGHT_ALIGNED_COLUMNS.includes(TransferStockColumnKeys[key]) &&
          'right'
      })),
      pagination: {
        page: pagination.page,
        size: pagination.size,
        pageCount: pagination.page_count,
        totalCount: pagination.total_count
      }
    });
  }

  static generateEmpty() {
    return new TransferStock({
      lastUpdate: '',
      stockLocationItems: [],
      columns: [],
      pagination: { page: 1, size: 10, pageCount: 0, totalCount: 0 }
    });
  }
}

export default TransferStock;
